import * as c from "./videoModal.module.scss";
import React, { useEffect, useState, useCallback } from "react";
import useModal from "@hooks/useModal";
import classNames from "classnames";
import FlowStepVideoSelect from "@components/flows/shared/flowStepVideoSelect";
import haircutsImg from "@images/flows/services/haircuts.svg";
import skincareImg from "@images/flows/services/skin.svg";
import makeupImg from "@images/flows/services/makeup.svg";
import barberingImg from "@images/flows/services/barbering.svg";
import hairRemovalImg from "@images/flows/services/hair-removal.svg";
import massagesImg from "@images/flows/services/massage.svg";
import tattoosImg from "@images/flows/services/tattoos.svg";
import nailsImg from "@images/flows/services/nail-services.svg";
import injectablesImg from "@images/flows/services/injectables.svg";
import spasImg from "@images/flows/services/dayspa.svg";
import lashesImg from "@images/flows/services/lashes.svg";
import otherImg from "@images/flows/services/other.svg";
import { Script } from "gatsby";

export default function VideoModal({ label, id }) {
  const { Modal, openModal, isModalOpen, closeModal } = useModal({ closeOnOverlayClick: true });
  const [videoSelected, setVideoSelected] = useState(true);
  const [wistiaVideoId, setWistiaVideoId] = useState(null);

  const options = [
    { label: "Hair Salon", value: "Hair Salon", img: haircutsImg, videoId: "1i004c236v" },
    { label: "Barbershop", value: "Barbershop", img: barberingImg, videoId: "1i004c236v" },
    { label: "Spa", value: "Spa", img: spasImg, videoId: "ih9en6iysa" },
    { label: "Beauty Studio", value: "Beauty Studio", img: makeupImg, videoId: "ih9en6iysa" },
    { label: "Skincare Studio", value: "Skincare Studio", img: skincareImg, videoId: "ih9en6iysa" },
    { label: "Hair Removal", value: "Hair Removal Studio", img: hairRemovalImg, videoId: "ih9en6iysa" },
    { label: "Nail Salon", value: "Nail Salon", img: nailsImg, videoId: "1i004c236v" },
    { label: "Massage Studio", value: "Massage Studio", img: massagesImg, videoId: "ih9en6iysa" },
    { label: "Tattoo & Piercing", value: "Tattoo & Piercing Studio", img: tattoosImg, videoId: "ih9en6iysa" },
    { label: "Med Spa", value: "Med Spa", img: injectablesImg, videoId: "0trz1kpypp" },
    { label: "Wellness Center", value: "Wellness Center", img: lashesImg, videoId: "0trz1kpypp" },
    { label: "Other", value: "Other", img: otherImg, videoId: "ih9en6iysa" },
  ];

  const setVideo = useCallback(
    (value) => {
      const matchingOption = options.find((option) => option.value === String(value));
      if (matchingOption.videoId) {
        try {
          window.localStorage.setItem("videoId", matchingOption.videoId);
        } catch (error) {
          console.error("Error setting localStorage item:", error);
        }

        return setWistiaVideoId(matchingOption.videoId);
      }
    },
    [options, wistiaVideoId]
  );

  const VideoModalContent = ({ isOpen, onSelected }) => {
    const onContinue = (value) => {
      try {
        window.localStorage.setItem("businessType", value);
      } catch (error) {
        console.error("Error setting localStorage item:", error);
      }
      setVideo(value);
      setVideoSelected(true);
      closeModal();
    };

    return (
      <div className={c.modalWrapper}>
        <div className={classNames(c.modalContent)}>
          <h2>Which best describes your business?</h2>
          <FlowStepVideoSelect
            minimumSelection={1}
            className={"videoModal"}
            onContinue={onContinue}
            onSelected={onSelected}
            options={options}
            buttonLabel={"Watch video"}
            singleSelect={true}
            wistiaVideoId={wistiaVideoId}
            videoIds={options.map((option) => option.videoId)}
          />
        </div>
      </div>
    );
  };

  useEffect(() => {
    try {
      if (window.localStorage.getItem("videoId")) {
        // Set the video based on localStorage value
        setWistiaVideoId(window.localStorage.getItem("videoId"));
        setVideoSelected(true);
      } else {
        setVideoSelected(false);
      }
    } catch (error) {
      console.error("Error getting localStorage item:", error);
    }
  }, [setVideoSelected, setVideo, wistiaVideoId]);

  return (
    <>
      <div className={classNames(c.videoButtonWrap, wistiaVideoId && c.active)}>
        <Script src={"https://fast.wistia.com/assets/external/E-v1.js"} async strategy="idle" />
        <div
          // Ensure unique ids for each video button
          id={id ? wistiaVideoId + "-" + id : wistiaVideoId}
          className={classNames(
            // It's necesssary to not set these IDs if the video is null to avoid error messaging from Wistia
            wistiaVideoId && "wistia_embed",
            wistiaVideoId && `wistia_async_${wistiaVideoId}`,
            "seo=false",
            "videoFoam=true",
            "popover=true",
            "popoverContent=link",
            c.videoButton,
            "videoSelectionButton"
          )}
        >
          <svg viewBox="0 0 23 23">
            <circle fill="none" strokeWidth={2} cx="11.5" cy="11.5" r="10.5" />
            <path d="M8.5,8.3 C8.5,7.74771525 8.94771525,7.3 9.5,7.3 C9.68741972,7.3 9.87106734,7.35266944 10.0299989,7.4520017 L15.1432027,10.6477541 C15.6115392,10.9404644 15.7539124,11.5574148 15.4612021,12.0257513 C15.3807214,12.1545204 15.2719718,12.26327 15.1432027,12.3437507 L10.0299989,15.539503 L10.0299989,15.539503 C9.87106734,15.6388353 9.68741972,15.6915047 9.5,15.6915047 C8.98716416,15.6915047 8.56449284,15.3054645 8.50672773,14.8081258 L8.5,14.6915047 L8.5,8.3 Z" />
          </svg>
          {label}
        </div>
        <button className={classNames(c.videoButton, "videoSelectionButton")} onClick={openModal}>
          <svg viewBox="0 0 23 23">
            <circle fill="none" strokeWidth={2} cx="11.5" cy="11.5" r="10.5" />
            <path d="M8.5,8.3 C8.5,7.74771525 8.94771525,7.3 9.5,7.3 C9.68741972,7.3 9.87106734,7.35266944 10.0299989,7.4520017 L15.1432027,10.6477541 C15.6115392,10.9404644 15.7539124,11.5574148 15.4612021,12.0257513 C15.3807214,12.1545204 15.2719718,12.26327 15.1432027,12.3437507 L10.0299989,15.539503 L10.0299989,15.539503 C9.87106734,15.6388353 9.68741972,15.6915047 9.5,15.6915047 C8.98716416,15.6915047 8.56449284,15.3054645 8.50672773,14.8081258 L8.5,14.6915047 L8.5,8.3 Z" />
          </svg>
          {label}
        </button>
      </div>
      {isModalOpen && (
        <Modal>
          <VideoModalContent isOpen={isModalOpen} />
        </Modal>
      )}
    </>
  );
}
